// Каталог оружия на клиенте — зеркало server/internal/game/weapons.go
// (id, темп, очередь/одиночные, магазины). Менять парой с сервером.
//
// Здесь же всё, что нужно только клиенту: русские имена для HUD, модели и
// иконки, звуки, параметры вида от первого лица и хвата в третьем лице.

import { PickupType } from "../net/protocol";

export const WeaponId = {
  Fists: 0,
  Chainsaw: 1,
  Shotgun: 2,
  Rifle: 3,
  Sniper: 4,
  Flamethrower: 5,
  Laser: 6,
  Rocket: 7,
  Grenade: 8,
} as const;
export type WeaponIdValue = typeof WeaponId[keyof typeof WeaponId];

export const WEAPON_COUNT = 9;

/** Биты 12..15 Input.buttons: выбор оружия (id+1), 0 = не менять.
 *  Сервер: game.WeaponSelectShift. */
export const WEAPON_SELECT_SHIFT = 12;
/** Пауза смены оружия (тики) — server WeaponSwitchTicks. */
export const WEAPON_SWITCH_TICKS = 18;

export type WeaponKind = "melee" | "hitscan" | "pellets" | "beam" | "cone" | "projectile";

/** Хват в руках от первого лица: какие руки и как держит. */
export type FpGrip = "fists" | "twoHand" | "oneHandThrow" | "saw";

export interface WeaponDef {
  id: number;
  /** ключ модели/иконки и имя в level-pack (weapon_<key>) */
  key: string;
  /** Имя для HUD (по-русски). */
  title: string;
  kind: WeaponKind;
  cooldownTicks: number;
  auto: boolean;
  /** -1 = бесконечно */
  maxAmmo: number;
  /** Число дробин (дробовик). */
  pellets: number;
  /** Модель (одна на всё: вид от первого лица, в руках у других, на полу). */
  model: string | null;
  icon: string;
  grip: FpGrip;
  /** Вид от первого лица: смещение модели от камеры (м, вправо/вниз/вперёд)
   *  и масштаб поверх метрического размера модели. */
  fp: { right: number; down: number; fwd: number; scale: number; recoil: number; kick: number };
  /** Третье лицо: доп. поворот/сдвиг в кисти (градусы, м), масштаб. */
  tp: { pitch: number; yaw: number; roll: number; fwd: number; up: number; side: number; scale: number };
  /** Светится ли на полу этим цветом (ореол подбора). */
  glow: [number, number, number];
  /** Звук выстрела и его скорость воспроизведения. */
  sound: string;
  soundRate: number;
  soundVol: number;
}

const FP_DEFAULT = { right: 0.13, down: 0.15, fwd: 0.32, scale: 1, recoil: 0.05, kick: 0.06 };
const TP_DEFAULT = { pitch: 0, yaw: 0, roll: 0, fwd: 0, up: 0, side: 0, scale: 1 };

export const WEAPONS: WeaponDef[] = [
  {
    id: WeaponId.Fists, key: "fists", title: "Кулаки", kind: "melee",
    cooldownTicks: 20, auto: false, maxAmmo: -1, pellets: 0,
    model: null, icon: "/models/weapons/icons/fists.png", grip: "fists",
    fp: { ...FP_DEFAULT, recoil: 0, kick: 0 }, tp: { ...TP_DEFAULT },
    glow: [1.0, 0.8, 0.5],
    sound: "/sounds/weapons/punch.wav", soundRate: 1, soundVol: 0.7,
  },
  {
    id: WeaponId.Chainsaw, key: "chainsaw", title: "Бензопила", kind: "melee",
    cooldownTicks: 5, auto: true, maxAmmo: 150, pellets: 0,
    model: "/models/weapons/chainsaw.glb", icon: "/models/weapons/icons/chainsaw.png", grip: "saw",
    fp: { right: 0.16, down: 0.2, fwd: 0.38, scale: 1, recoil: 0.006, kick: 0.004 },
    tp: { ...TP_DEFAULT },
    glow: [1.0, 0.55, 0.2],
    sound: "/sounds/weapons/chainsaw.wav", soundRate: 1, soundVol: 0.5,
  },
  {
    id: WeaponId.Shotgun, key: "shotgun", title: "Дробовик", kind: "pellets",
    cooldownTicks: 72, auto: false, maxAmmo: 24, pellets: 9,
    model: "/models/weapons/shotgun.glb", icon: "/models/weapons/icons/shotgun.png", grip: "twoHand",
    fp: { ...FP_DEFAULT, recoil: 0.11, kick: 0.16 }, tp: { ...TP_DEFAULT },
    glow: [1.0, 0.75, 0.3],
    sound: "/sounds/weapons/shotgun.wav", soundRate: 1, soundVol: 0.75,
  },
  {
    id: WeaponId.Rifle, key: "rifle", title: "Автомат", kind: "hitscan",
    cooldownTicks: 22, auto: true, maxAmmo: 50, pellets: 0,
    model: "/models/weapons/rifle.glb", icon: "/models/weapons/icons/rifle.png", grip: "twoHand",
    fp: { ...FP_DEFAULT }, tp: { ...TP_DEFAULT },
    glow: [1.0, 0.8, 0.35],
    sound: "/sounds/fire.mp3", soundRate: 1, soundVol: 0.55,
  },
  {
    id: WeaponId.Sniper, key: "sniper", title: "Снайперка", kind: "hitscan",
    cooldownTicks: 90, auto: false, maxAmmo: 15, pellets: 0,
    model: "/models/weapons/sniper.glb", icon: "/models/weapons/icons/sniper.png", grip: "twoHand",
    fp: { ...FP_DEFAULT, recoil: 0.13, kick: 0.12 }, tp: { ...TP_DEFAULT },
    glow: [0.6, 0.85, 1.0],
    sound: "/sounds/weapons/sniper.wav", soundRate: 1, soundVol: 0.8,
  },
  {
    id: WeaponId.Flamethrower, key: "flamethrower", title: "Огнемёт", kind: "cone",
    cooldownTicks: 4, auto: true, maxAmmo: 200, pellets: 0,
    model: "/models/weapons/flamethrower.glb", icon: "/models/weapons/icons/flamethrower.png", grip: "twoHand",
    fp: { ...FP_DEFAULT, recoil: 0.004, kick: 0.003 }, tp: { ...TP_DEFAULT },
    glow: [1.0, 0.45, 0.1],
    sound: "/sounds/weapons/flame.wav", soundRate: 1, soundVol: 0.45,
  },
  {
    id: WeaponId.Laser, key: "laser", title: "Лазер", kind: "beam",
    cooldownTicks: 6, auto: true, maxAmmo: 100, pellets: 0,
    model: "/models/weapons/laser.glb", icon: "/models/weapons/icons/laser.png", grip: "twoHand",
    fp: { ...FP_DEFAULT, recoil: 0.01, kick: 0.01 }, tp: { ...TP_DEFAULT },
    glow: [1.0, 0.25, 0.2],
    sound: "/sounds/weapons/laser.wav", soundRate: 1, soundVol: 0.4,
  },
  {
    id: WeaponId.Rocket, key: "rocket", title: "Ракетница", kind: "projectile",
    cooldownTicks: 60, auto: false, maxAmmo: 10, pellets: 0,
    model: "/models/weapons/rocket.glb", icon: "/models/weapons/icons/rocket.png", grip: "twoHand",
    fp: { ...FP_DEFAULT, recoil: 0.09, kick: 0.08 }, tp: { ...TP_DEFAULT },
    glow: [1.0, 0.6, 0.25],
    sound: "/sounds/weapons/rocket.wav", soundRate: 1, soundVol: 0.7,
  },
  {
    id: WeaponId.Grenade, key: "grenade", title: "Граната", kind: "projectile",
    cooldownTicks: 45, auto: false, maxAmmo: 6, pellets: 0,
    model: "/models/weapons/grenade.glb", icon: "/models/weapons/icons/grenade.png", grip: "oneHandThrow",
    fp: { right: 0.16, down: 0.17, fwd: 0.3, scale: 1, recoil: 0, kick: 0 }, tp: { ...TP_DEFAULT },
    glow: [0.7, 1.0, 0.45],
    sound: "/sounds/weapons/throw.wav", soundRate: 1, soundVol: 0.6,
  },
];

export function weaponDef(id: number): WeaponDef {
  return WEAPONS[id] ?? WEAPONS[WeaponId.Rifle]!;
}

/** id оружия по типу pickup'а; -1 — не оружие. */
export function weaponFromPickupType(t: number): number {
  if (t === PickupType.WeaponRifle) return WeaponId.Rifle;
  if (t >= PickupType.WeaponBase && t < PickupType.WeaponBase + WEAPON_COUNT) {
    return t - PickupType.WeaponBase;
  }
  return -1;
}

/** Порядок «лучшее оружие» для автосмены при пустом магазине. */
export const AUTO_SWITCH_ORDER: number[] = [
  WeaponId.Rifle, WeaponId.Shotgun, WeaponId.Laser, WeaponId.Sniper,
  WeaponId.Flamethrower, WeaponId.Chainsaw, WeaponId.Rocket, WeaponId.Grenade,
  WeaponId.Fists,
];
